/**
 * Layout de colunas (ordem, visibilidade, largura) salvo por usuário.
 * Colunas persist:false (checkbox de seleção, _id da busca) ficam fora do layout e na frente.
 */
export const MIN_WIDTH = 40;
export const MAX_WIDTH = 2000;

const isPersistable = (column) => column.persist !== false;

export const clampWidth = (width) =>
    Number.isFinite(width) ? Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(width))) : null;

export const mergeLayout = (columns, saved) => {
    const persistable = columns.filter(isPersistable);
    const known = new Set(persistable.map((column) => column.key));
    const savedColumns = (Array.isArray(saved?.columns) ? saved.columns : []).filter((c) => known.has(c?.key));
    const savedByKey = new Map(savedColumns.map((c) => [c.key, c]));

    const columnOrder = savedColumns.map((c) => c.key);
    persistable.forEach((column, index) => {
        if (savedByKey.has(column.key)) return;
        let insertAt = 0;
        for (let i = index - 1; i >= 0; i -= 1) {
            const position = columnOrder.indexOf(persistable[i].key);
            if (position !== -1) {
                insertAt = position + 1;
                break;
            }
        }
        columnOrder.splice(insertAt, 0, column.key);
    });

    const columnVisibility = {};
    const columnSizing = {};
    for (const column of columns) {
        const stored = savedByKey.get(column.key);
        columnVisibility[column.key] = typeof stored?.visible === "boolean" ? stored.visible : column.visible !== false;
        const width = clampWidth(stored?.width) ?? column.width;
        if (width) columnSizing[column.key] = width;
    }
    return { columnOrder, columnVisibility, columnSizing };
};

export const withFixedColumns = (columns, columnOrder) => [
    ...columns.filter((column) => !isPersistable(column)).map((column) => column.key),
    ...columnOrder.filter((key) => columns.some((column) => column.key === key && isPersistable(column))),
];

export const toSavedLayout = (columns, { columnOrder = [], columnVisibility = {}, columnSizing = {} }) => {
    const persistable = columns.filter(isPersistable).map((column) => column.key);
    const ordered = [
        ...columnOrder.filter((key) => persistable.includes(key)),
        ...persistable.filter((key) => !columnOrder.includes(key)),
    ];
    return {
        columns: ordered.map((key) => ({
            key,
            visible: columnVisibility[key] !== false,
            width: clampWidth(columnSizing[key]),
        })),
    };
};

export const moveColumn = (order, fromKey, toKey) => {
    const from = order.indexOf(fromKey);
    const to = order.indexOf(toKey);
    if (from === -1 || to === -1 || from === to) return order;
    const next = [...order];
    next.splice(from, 1);
    next.splice(to, 0, fromKey);
    return next;
};
